import UrlHelpers from "./url";
import {FieldHandler} from "./fieldHandler";
import {i18n} from "./i18n";
import {deleteIcon, moveOrgaIcon, overwriteIcon, permIcon} from "./icons";
import {Notification} from "./notification";
import {setContextActions} from "./contextActions";
import {organization_created, tenant_created, tenant_deleted, tenant_updated} from "./dapiEventUris";
import {ErrorHandler} from "./errorHandler";
import {subscriptionRequestDto} from "./app";

export class Tenant {
    public timer: number;

    private readonly _isSysAdmin: boolean;

    private _dapi: Dapi = (<any>window).dapi;
    private _dux = (<any>window).dux;
    private _i18n: i18n = new i18n();

    constructor() {
        this._isSysAdmin = !!UrlHelpers.getMetaData("sysAdmin");
        this._addCtxActions();
        this._dapi.addResourceEventListener('changed', this.handleChangeEvent);
        this.listenEditOverwriteEvents();
        this.timer = -1;
        (<HTMLAnchorElement>document.getElementById('btnSave')).addEventListener('click', this._saveClick);
        (<HTMLInputElement>document.getElementById('tenantDomainValue')).addEventListener("keyup", this._checkSubdomain);
        (<HTMLInputElement>document.getElementById('tenantDomainValue')).addEventListener("change", this._checkSubdomain);
        const btnWithdrawTermination = <HTMLButtonElement>document.getElementById('btn_tenantTermination_recover');
        if (btnWithdrawTermination) {
            btnWithdrawTermination.addEventListener('click', this._recoverTenant);
        }
        const btnTerminateTenant = <HTMLButtonElement>document.getElementById('btn_tenantTermination_terminate');
        if (btnTerminateTenant) {
            btnTerminateTenant.addEventListener('click', this._terminateTenantWithModal)
        }
        this._addUnsubscribeEvents();
    }

    private listenEditOverwriteEvents = () => {
        const anchorElements = document.querySelectorAll('.edit-overwrite-link');
        anchorElements.forEach(a => {
            a.addEventListener('click', this.handleEditOverwrite);
        });
    }

    private handleEditOverwrite = () => {
        const url = UrlHelpers.getLinkRelHref('overwrites');
        this._dapi.navigate(url);
    };

    private handleChangeEvent = (resouceEvent: IResourceEvent) => {
        switch (resouceEvent.uri) {
            case tenant_updated:
                window.location.reload();
                break;
            case tenant_deleted:
                this._dapi.closeResourceIfMainResource();
        }
    };

    private _addUnsubscribeEvents = () => {
        let unsubscribeButtons = document.getElementsByClassName('btn-remove');
        for (let i = 0; i < unsubscribeButtons.length; i++) {
            let btn = unsubscribeButtons[i];
            if (!btn.hasAttribute('disabled')) {
                btn.addEventListener('click', this._btn_unsubscribeApp_click);
            }
        }
    };

    private _btn_unsubscribeApp_click = (e: any) => {
        let target = <HTMLButtonElement>e.currentTarget;
        let appName = target.dataset.name;
        let tenantId = UrlHelpers.getMetaData('tenantId');
        const dependants = this._getDependantsForApp(appName);
        if (dependants && dependants.length > 0) {
            this._show_unsubscribeNotAllowed_dialog(appName, dependants);
            return;
        }
        this._show_unsubscribeApp_dialog(appName, tenantId);
    };

    private _getDependantsForApp = (appName: string): string[] => {
        let dependencies = UrlHelpers.getMetaData('tenantDependencies');
        if (dependencies) {
            let dependants = JSON.parse(dependencies);
            return dependants[appName] ? dependants[appName] : null;
        }
        return null;
    };

    private _show_unsubscribeNotAllowed_dialog = (appName: string, dependants: string[]) => {
        let msg = this._i18n.getTranslation('tenantapp_dialog_unsubscribe_dependency_information');
        if (dependants && dependants.length > 0) {
            dependants.forEach((v, i) => {
                if (i > 0) {
                    msg = msg + ',';
                }
                msg = msg + ` ${v}`;
            });
        }
        this._dux.query.notifier(msg, {
            title: this._i18n.getTranslation('tenantapp_dialog_unsubscribeNotAllowed_title'),
            showClose: true,
            submit: this._i18n.getTranslation('tenantapp_dialog_unsusbscribeNotAllowed_btn_title'),
            priority: 'danger',
            callback: () => {
                console.log(`App ${appName} is a dependency for apps ${dependants} and must not be unsubscribed.`);
            }
        });
    };

    private _show_unsubscribeApp_dialog = (appName: string, tenantId: string) => {
        let msg = i18n.replaceTranslationPlaceholder(this._i18n.getTranslation('tenantapp_dialog_unsubscribe_alert'), '{appName}', appName);
        let title = this._i18n.getTranslation('tenantapp_dialog_unsubscribe_title');
        this._dux.query.notifier(msg, {
            title: title,
            showClose: true,
            submit: this._i18n.getTranslation('tenantapp_dialog_unsusbscribe_btn_unsubscribe_title'),
            priority: 'warning',
            callback: () => {
                this._execute_unsubscribe_app(appName, tenantId);
            }
        });
    };

    private _execute_unsubscribe_app = (appName: string, tenantId: string) => {
        // alert(tenantId+" => unsubscribe app "+appName);
        let dto: subscriptionRequestDto = {
            appName: appName,
            tenantId: tenantId,
            action: 'unsubscribe'
        };
        let url = UrlHelpers.getLinkRelHref('subscription');
        let xhr = new XMLHttpRequest();
        xhr.open('PUT', url);
        xhr.onload = () => {
            if (xhr.status === 200) {
                let msg = this._i18n.getTranslation('tenantapp_notification_unsubscribe_success_msg');
                let title = this._i18n.getTranslation('tenantapp_notification_unsubscribe_success_title');
                Notification.showSuccessNotification(title, msg);
                setTimeout(() => {
                    location.reload(true);
                }, 3200);
            } else {
                let errorDto = ErrorHandler.parseError(xhr.responseText);
                ErrorHandler.showErrorDialogByDto(errorDto, 'danger', this._dux);
            }
        };
        let data = JSON.stringify(dto);
        xhr.send(data);
    };

    private _saveClick = () => {
        let data = this._getValues();
        if (data === null) {
            console.error("Domain was null or empty!");
            return
        }
        let url = UrlHelpers.getLinkRelHref("self");
        let xhr = new XMLHttpRequest();
        xhr.open('PUT', url);
        xhr.setRequestHeader('accept', 'application/json');
        xhr.onload = () => {
            if (xhr.status === 200) {
                this._dapi.dispatchResourceEvent({
                    name: 'changed',
                    uri: url
                });
                Notification.showSuccessNotification(this._i18n.getTranslation('tenant_save_success_title'), this._i18n.getTranslation('tenant_save_success_message'));
            } else if (xhr.status === 404) {
                console.error("tenant not found: " + xhr.responseText);
                let error = ErrorHandler.parseError(xhr.responseText);
                if (error) {
                    Notification.showErrorNotification(error.title, error.description);
                }
            } else {
                console.error("Unknown error: ", xhr.responseText);
                Notification.showErrorNotification(this._i18n.getTranslation('tenant_save_error_title'), this._i18n.getTranslation('tenant_save_error_message'));
            }
        };
        let json = JSON.stringify(data);
        xhr.send(json);
    };

    private _recoverTenant = () => {
        const request = new XMLHttpRequest();
        const url = UrlHelpers.getLinkRelHref('terminate');
        const data: tenantTerminationDto = {
            state: TenantTerminationState.notTerminated
        }
        const selfUrl = UrlHelpers.getLinkRelHref("self");
        request.open('POST', url);
        request.setRequestHeader('Content-Type', 'application/json')
        request.onload = () => {
            if (request.status != 200) {
                Notification.showErrorNotification(this._i18n.getTranslation('tenant_recover_failed_title'), this._i18n.getTranslation('tenant_recover_failed_message'));
                console.log("recover tenant failed - response code: " + request.status);
                return;
            }
            this._dapi.dispatchResourceEvent({
                name: 'changed',
                uri: selfUrl
            });
            Notification.showSuccessNotification(this._i18n.getTranslation('tenant_recover_success_title'), this._i18n.getTranslation('tenant_recover_success_message'));
            const waitForNotificationAnimation: number = 3100;
            setTimeout(() => {
                location.reload();
            }, waitForNotificationAnimation);
        }
        request.send(JSON.stringify(data));
    }

    private _terminateTenantWithModal = () => {
        this._dux.query.notifier(this._i18n.getTranslation('tenantTermination_modal_terminate_tenant_message'), {
            title: this._i18n.getTranslation('tenantTermination_modal_terminate_tenant_title'), //"Wirklich löschen?",
            showClose: true,
            submit: this._i18n.getTranslation('tenantTermination_modal_terminate_tenant_terminate_btn_label'), //"Löschen",
            priority: "warning",
            callback: () => {
                this._terminateTenant();
            }
        });
    };

    private _terminateTenant = () => {
        const sysAdmin = !!UrlHelpers.getMetaData('sysAdmin');
        const tenantAdmin = !!UrlHelpers.getMetaData('tenantAdmin');
        if (!sysAdmin && !tenantAdmin) {
            console.log("user is neither sysAdmin nor tenantAdmin");
            Notification.showErrorNotification(this._i18n.getTranslation('tenant_termination_failed_title'), this._i18n.getTranslation('tenant_termination_failed_message'));
            return;
        }
        const request = new XMLHttpRequest();
        const url = UrlHelpers.getLinkRelHref('terminate');

        let data: tenantTerminationDto = null;

        if (tenantAdmin) {
            data = {state: TenantTerminationState.byCustomer};
        }
        if (sysAdmin) {
            data = {state: TenantTerminationState.byAdmin};
        }

        const selfUrl = UrlHelpers.getLinkRelHref("self");
        request.open('POST', url);
        request.setRequestHeader('Content-Type', 'application/json')
        request.onload = () => {
            if (request.status != 200) {
                Notification.showErrorNotification(this._i18n.getTranslation('tenant_termination_failed_title'), this._i18n.getTranslation('tenant_termination_failed_message'));
                console.log("recover tenant failed - response code: " + request.status);
                return;
            }
            this._dapi.dispatchResourceEvent({
                name: 'changed',
                uri: selfUrl
            });
            Notification.showSuccessNotification(this._i18n.getTranslation('tenant_termination_success_title'), this._i18n.getTranslation('tenant_termination_success_message'));
            const waitForNotificationAnimation: number = 3100;
            setTimeout(() => {
                location.reload();
            }, waitForNotificationAnimation);
        }
        request.send(JSON.stringify(data));
    };

    private _getValues = (): tenantDto => {
        let subdomain = FieldHandler.getFieldValue("tenantDomainValue");
        if (subdomain.isNullOrEmpty()) {
            let btnSave = <HTMLAnchorElement>document.getElementById("btnSave");
            let subdomainField = <HTMLInputElement>document.getElementById("tenantDomainValue");
            let subdomainMessageField = document.getElementById("tenantDomainMessage");
            let subdomainIconField = document.getElementById("tenantDomainIcon");
            this._checkSubdomainFail(subdomainField, subdomainIconField, btnSave, subdomainMessageField, this._i18n.getTranslation('tenant_subdomain_error_field_empty'));
            return null;
        }

        let dto: tenantDto = {
            tenant: {
                id: FieldHandler.getFieldValue("tenantIdValue"),
                name: FieldHandler.getFieldValue("tenantNameValue"),
                domainName: subdomain,
                organizationId: UrlHelpers.getLinkDataDetails("self"),
            }
        };

        return dto;
    };

    private _addCtxActions = () => {
        let ctx = Array<IContextAction>();
        ctx.push({
            title: this._i18n.getTranslation('permission_tenant_title'),
            icon: permIcon,
            href: UrlHelpers.getLinkRelHref('permission')
        });

        ctx.push({
            title: this._i18n.getTranslation('tenant_edit_ctxAction_overwrite_label'),
            icon: overwriteIcon,
            href: UrlHelpers.getLinkRelHref('overwrites')
        });

        var furtherActions = document.querySelectorAll("link[rel='centerextension']");
        furtherActions.forEach((action) => {
            ctx.push({
                title: action.getAttribute('title'),
                icon: action.getAttribute('data-icon'),
                href: action.getAttribute('href')
            })
        });

        if (this._isSysAdmin) {
            ctx.push({
                title: this._i18n.getTranslation('organization_details_tenantList_btn_moveTenant'),
                icon: moveOrgaIcon,
                href: UrlHelpers.getLinkRelHref('tenantOrganization')
            });
            ctx.push({
                title: this._i18n.getTranslation('organization_details_tenantList_btn_delete'),
                icon: deleteIcon,
                callback: () => {
                    this._deleteTenant();
                }
            })
        }
        // this._dapi.setContextActions(ctx);
        setContextActions(this._dapi, this._i18n, ctx);
    };

    private _deleteTenant = (): void => {
        this._dux.query.notifier(this._i18n.getTranslation('tenant_modal_delete_tenant_message'), {
            title: this._i18n.getTranslation('tenant_modal_delete_tenant_title'), //"Wirklich löschen?",
            showClose: true,
            submit: this._i18n.getTranslation('tenant_modal_delete_tenant_btn_delete_label'), //"Löschen",
            priority: "warning",
            callback: () => {
                this._deleteElement();
            }
        });
    };

    private _deleteElement = () => {
        let url = UrlHelpers.getLinkRelHref('self');
        let xhr = new XMLHttpRequest();
        xhr.open("DELETE", url);
        xhr.setRequestHeader('accept', 'application/json');
        xhr.onload = () => {
            if (xhr.status === 204) {
                this._dapi.dispatchResourceEvent({
                    name: "deleted",
                    uri: tenant_deleted //'/center/tenant/deleted'//url
                });
                this._dapi.closeResourceIfMainResource();
            } else if (xhr.status === 401) {
                Notification.showErrorNotification(this._i18n.getTranslation('tenant_delete_error_unauthorized_title'), this._i18n.getTranslation('tenant_delete_error_unauthorized_message'));
            } else {
                console.error(xhr.responseText);
                Notification.showErrorNotification(this._i18n.getTranslation('tenant_delete_error_unexpected_error_title'), this._i18n.getTranslation('tenant_delete_error_unexpected_error_message'));
            }
        };
        try {
            xhr.send();
        } catch (exp) {
            console.error(exp);
        }
    };

    // domain check event
    private _checkSubdomain = () => {
        let btnSave = <HTMLAnchorElement>document.getElementById("btnSave");
        let btnSaveOriginalValue = "<i class=\"dv dv-save\"></i>&nbsp;" + this._i18n.getTranslation('btn_save_title');
        let subdomainField = <HTMLInputElement>document.getElementById("tenantDomainValue");
        let subdomain = subdomainField.value;
        let subdomainMessageField = document.getElementById("tenantDomainMessage");
        let subdomainIconField = document.getElementById("tenantDomainIcon");

        subdomainMessageField.innerText = "";
        if (subdomainMessageField.children.length > 0) {
            //subdomainMessageField.children.clear();
        }

        this._removeIcons(subdomainField, subdomainIconField);
        btnSave.text = this._i18n.getTranslation('tenant_checkdomain_btn_save_waittitle');
        btnSave.setAttribute('disabled', 'disabled');
        if (subdomain.isNullOrEmpty()) {// === undefined || subdomain === null || subdomain === '') {
            //this._checkSubdomainSuccess(subdomainField, subdomainIconField, btnSave, btnSaveOriginalValue);
            this._checkSubdomainFail(subdomainField, subdomainIconField, btnSave, subdomainMessageField, this._i18n.getTranslation('tenant_subdomain_error_field_empty'));
            clearTimeout(this.timer);
        } else if (this.timer < 1) {
            this._addSpinner(subdomainIconField);
            this.timer = setTimeout(() => {
                subdomain = subdomainField.value;
                if (subdomain.isNullOrEmpty()) {// === undefined || subdomain === null || subdomain === '') {
                    this._checkSubdomainFail(subdomainField, subdomainIconField, btnSave, subdomainMessageField, this._i18n.getTranslation('tenant_subdomain_error_field_empty'));
                    clearTimeout(this.timer);
                    this.timer = -1;
                } else {
                    let validSubdomain = this._cleanSubdomain(subdomain);
                    if (subdomain !== validSubdomain) {
                        this._checkSubdomainFail(subdomainField, subdomainIconField, btnSave, subdomainMessageField, this._i18n.getTranslation('tenant_subdomain_error_invalidchar'));
                    } else if (subdomain.length < 3) {
                        this._checkSubdomainFail(subdomainField, subdomainIconField, btnSave, subdomainMessageField, this._i18n.getTranslation('tenant_subdomain_error_minlength'));
                    } else {
                        this._checkSubdomainExist(subdomain, subdomainField, subdomainIconField, btnSave, btnSaveOriginalValue, subdomainMessageField);
                    }
                    clearTimeout(this.timer);
                    this.timer = -1;
                }
            }, 500);
        }
    };

    private _checkSubdomainExist = (subdomain: string, subdomainField: HTMLInputElement, subdomainIconField: HTMLSpanElement, btnSave: HTMLAnchorElement, btnSendOriginalValue: string, subdomainMessageField: HTMLSpanElement) => {
        let tenantId = FieldHandler.getFieldValue("tenantIdValue");
        let url = UrlHelpers.getLinkRelHref("domainCheck");
        let xhr = new XMLHttpRequest();
        xhr.open('POST', url);
        xhr.setRequestHeader('accept', 'application/json');
        xhr.onload = () => {
            if (xhr.status === 200) {
                var result = JSON.parse(xhr.responseText);
                if (!result.exists) {
                    this._checkSubdomainSuccess(subdomainField, subdomainIconField, btnSave, btnSendOriginalValue);
                } else {
                    if (result.message === 'Domain already exists.') {
                        this._checkSubdomainFail(subdomainField, subdomainIconField, btnSave, subdomainMessageField, this._i18n.getTranslation('tenant_subdomain_error_domain_already_exist'));
                    } else if (result.message === 'Domain is blacklisted.') {
                        this._checkSubdomainFail(subdomainField, subdomainIconField, btnSave, subdomainMessageField, this._i18n.getTranslation('tenant_subdomain_error_domain_is_blacklisted'));
                    } else {
                        this._checkSubdomainFail(subdomainField, subdomainIconField, btnSave, subdomainMessageField, this._i18n.getTranslation('tenant_subdomain_error_domain_already_exist'));
                    }
                    console.warn(result.message);
                }
            } else {
                var result = JSON.parse(xhr.responseText);
                console.error(result.message);
                this._checkSubdomainFail(subdomainField, subdomainIconField, btnSave, subdomainMessageField, this._i18n.getTranslation('tenant_subdomain_error_unexpected_error'));
                if (result.message === "") {

                } else if (result.message === "Domain is blacklisted.") {
                    this._checkSubdomainFail(subdomainField, subdomainIconField, btnSave, subdomainMessageField, this._i18n.getTranslation('tenant_subdomain_error_domain_is_blacklisted'));
                } else {
                }
            }
        };
        xhr.send(JSON.stringify({
            domain: subdomain,
            tenantId: tenantId
        }));
    };

    private _cleanSubdomain = (value: string): string => {
        return value.replace(/[^a-zA-Z0-9-]/g, "").replace(/ +/, "");
    };

    private _removeIcons = (subdomainField: HTMLInputElement, subdomainIconField: HTMLSpanElement) => {
        subdomainField.classList.remove('has-success');
        subdomainField.classList.remove('has-error');
        subdomainIconField.classList.remove('dv-success');
        subdomainIconField.classList.remove('dv-warning');
    };

    private _addSpinner = (element: HTMLSpanElement) => {
        //element.classList.add('fa', 'fa-spinner', 'fa-spin');
        element.classList.add('dv', 'dv-waitingdots', 'dv-spin');
    };

    private _removeSpinner = (element: HTMLSpanElement) => {
        element.classList.remove('dv', 'dv-waitingdots', 'dv-spin');
    };

    private _checkSubdomainFail = (subdomainField: HTMLInputElement, subdomainIconField: HTMLSpanElement, btnSend: HTMLAnchorElement, subdomainMessageField: HTMLSpanElement, msg: string) => {
        this._removeSpinner(subdomainIconField);
        subdomainField.classList.add('has-error');
        subdomainIconField.classList.add('dv', 'dv-warning');
        subdomainMessageField.innerText = msg + ' ';
        btnSend.setAttribute('disabled', 'disabled');
        btnSend.text = this._i18n.getTranslation('tenant_checkdomain_btn_save_invalidDomain');
        btnSend.removeEventListener("click", this._saveClick);
        let formgrp = document.getElementById("tenantDomain");
        formgrp.classList.remove("has-success");
        formgrp.classList.add("has-error");
    };

    private _checkSubdomainSuccess = (subdomainField: HTMLInputElement, subdomainIconField: HTMLSpanElement, btnSend: HTMLAnchorElement, btnSendOriginalValue: string) => {
        this._removeSpinner(subdomainIconField);
        subdomainField.classList.add('has-success');
        subdomainIconField.classList.add('dv', 'dv-success');
        btnSend.innerHTML = btnSendOriginalValue;
        btnSend.removeAttribute('disabled');
        btnSend.addEventListener("click", this._saveClick);
        let formgrp = document.getElementById("tenantDomain");
        formgrp.classList.remove("has-error");
        formgrp.classList.add("has-success");
    };
}

export interface tenantDto {
    tenant: tenantDetailsDto;
}

export interface tenantDetailsDto {
    id: string;
    name: string;
    domainName: string;
    organizationId: string;
}

export interface tenantTerminationDto {
    state: TenantTerminationState
}

export enum TenantTerminationState {
    byCustomer = "TERMINATED_CUSTOMER",
    byAdmin = "TERMINATED_ADMIN",
    byBilling = "TERMINATED_BILLING",
    notTerminated = "NOT_TERMINATED"
}